import { useMemo, useState } from "react";
import { ProjectOverviewLink } from "@/components/ProjectOverviewLink";
import { Table, type Column } from "@/components/ui/Table";
import {
  buildEmployeeHoursRanking,
  buildProjectOpenHoursRanking,
  formatDurationHours,
  type EmployeeHoursEntry,
  type HeroUsageRow,
  type ProjectOpenHoursEntry,
} from "@/lib/auswertungen";
import { OpenHoursRankingDetailDialog } from "@/pages/auswertungen/OpenHoursRankingDetailDialog";

interface KeyedProjectRow extends ProjectOpenHoursEntry {
  __key: string;
  rank: number;
}

interface KeyedEmployeeRow extends EmployeeHoursEntry {
  __key: string;
  rank: number;
}

type DetailSelection =
  | { mode: "project"; project: ProjectOpenHoursEntry }
  | { mode: "employee"; partnerName: string }
  | null;

export function HeroUsageRankings({
  rows,
  year,
  onEntriesUpdated,
}: {
  rows: HeroUsageRow[];
  year: number;
  onEntriesUpdated?: () => void | Promise<void>;
}) {
  const [selection, setSelection] = useState<DetailSelection>(null);

  const projectRows = useMemo<KeyedProjectRow[]>(() => {
    return buildProjectOpenHoursRanking(rows).map((entry, index) => ({
      ...entry,
      rank: index + 1,
      __key: `${entry.auftragId ?? entry.projekt}:${index}`,
    }));
  }, [rows]);

  const employeeRows = useMemo<KeyedEmployeeRow[]>(() => {
    return buildEmployeeHoursRanking(rows).map((entry, index) => ({
      ...entry,
      rank: index + 1,
      __key: `${entry.partnerName}:${index}`,
    }));
  }, [rows]);

  const projectColumns = useMemo<Column<KeyedProjectRow>[]>(
    () => [
      {
        key: "rank",
        header: "#",
        sortable: true,
        sortValue: (row) => row.rank,
        render: (row) => <span className="tabular-nums text-label-secondary">{row.rank}</span>,
      },
      {
        key: "projekt",
        header: "Projekt",
        sortable: true,
        sortValue: (row) => row.projekt,
        render: (row) => (
          <ProjectOverviewLink auftragId={row.auftragId} label={row.projekt} />
        ),
      },
      {
        key: "openSeconds",
        header: "Ohne Stundennachweis",
        sortable: true,
        sortValue: (row) => row.openSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums font-medium text-status-red">
            {formatDurationHours(row.openSeconds)}
          </span>
        ),
      },
    ],
    [],
  );

  const employeeColumns = useMemo<Column<KeyedEmployeeRow>[]>(
    () => [
      {
        key: "rank",
        header: "#",
        sortable: true,
        sortValue: (row) => row.rank,
        render: (row) => <span className="tabular-nums text-label-secondary">{row.rank}</span>,
      },
      {
        key: "partnerName",
        header: "Mitarbeiter",
        sortable: true,
        sortValue: (row) => row.partnerName,
        render: (row) => <span className="font-medium text-tint">{row.partnerName}</span>,
      },
      {
        key: "unmappedSeconds",
        header: "Ohne Projekt",
        sortable: true,
        sortValue: (row) => row.unmappedSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.unmappedSeconds)}
          </span>
        ),
      },
      {
        key: "unassignedSeconds",
        header: "Ohne Nachweis",
        sortable: true,
        sortValue: (row) => row.unassignedSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.unassignedSeconds)}
          </span>
        ),
      },
      {
        key: "withProofSeconds",
        header: "Mit Nachweis",
        sortable: true,
        sortValue: (row) => row.withProofSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.withProofSeconds)}
          </span>
        ),
      },
      {
        key: "aufNachweisSeconds",
        header: "Auf Nachweis",
        sortable: true,
        sortValue: (row) => row.aufNachweisSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.aufNachweisSeconds)}
          </span>
        ),
      },
      {
        key: "festpreisSeconds",
        header: "Festpreis",
        sortable: true,
        sortValue: (row) => row.festpreisSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.festpreisSeconds)}
          </span>
        ),
      },
      {
        key: "ohneBerechnungSeconds",
        header: "Ohne Berechnung",
        sortable: true,
        sortValue: (row) => row.ohneBerechnungSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.ohneBerechnungSeconds)}
          </span>
        ),
      },
      {
        key: "gewaehrleistungSeconds",
        header: "Gewährleistung",
        sortable: true,
        sortValue: (row) => row.gewaehrleistungSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums">
            {formatDurationHours(row.gewaehrleistungSeconds)}
          </span>
        ),
      },
      {
        key: "openSeconds",
        header: "Offen gesamt",
        sortable: true,
        sortValue: (row) => row.openSeconds,
        render: (row) => (
          <span className="whitespace-nowrap tabular-nums font-medium text-status-red">
            {formatDurationHours(row.openSeconds)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="space-y-8">
        <section>
          <h2 className="text-base font-semibold text-label">Mitarbeiter-Ranking</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Aufschlüsselung nach Stunden ohne Projekt, ohne Nachweis sowie mit Nachweis oder
            Festpreis im Jahr {year}. Zeile anklicken für Details nach Projekt.
          </p>
          <div className="mt-3">
            <Table<KeyedEmployeeRow>
              columns={employeeColumns}
              rows={employeeRows}
              rowKey={(row) => row.__key}
              emptyText={`Keine Mitarbeiterdaten im Jahr ${year}.`}
              stickyHeader={false}
              onRowClick={(row) => setSelection({ mode: "employee", partnerName: row.partnerName })}
            />
          </div>
        </section>

        <section>
          <h2 className="text-base font-semibold text-label">
            Projekte mit den meisten offenen Stunden
          </h2>
          <p className="mt-1 text-sm text-label-secondary">
            Sortiert nach Stunden ohne Stundennachweis auf zugeordneten Projekten im Jahr {year}.
            Zeile anklicken für Details nach Mitarbeiter.
          </p>
          <div className="mt-3">
            <Table<KeyedProjectRow>
              columns={projectColumns}
              rows={projectRows}
              rowKey={(row) => row.__key}
              emptyText={`Keine offenen Projektstunden im Jahr ${year}.`}
              stickyHeader={false}
              onRowClick={(row) => setSelection({ mode: "project", project: row })}
            />
          </div>
        </section>
      </div>

      <OpenHoursRankingDetailDialog
        open={selection !== null}
        onClose={() => setSelection(null)}
        mode={selection?.mode ?? null}
        project={selection?.mode === "project" ? selection.project : null}
        partnerName={selection?.mode === "employee" ? selection.partnerName : null}
        rows={rows}
        onEntriesUpdated={onEntriesUpdated}
      />
    </>
  );
}
